import type { Place, RowBoundary, RackBoundary, PlaceDimensions } from '../types/warehouse.types';
import { alpha } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core';
import { getRowId } from '../utils/placeUtils';
import { BaseRenderer } from './base/BaseRenderer';
export interface Row2DProjectionRendererConfig {
  palletSpacing: number; // Small gap between columns
  levelSpacing: number; // Gap between levels (vertical)
  maxPalletSize: number; // Maximum size of a pallet
  topMargin: number; // Margin from top of canvas
  titleHeight: number; // Height of title area
  labelMargin: number; // Space for level labels on the left
  bottomLabelHeight: number; // Space for place number labels at the bottom
}

export class Row2DProjectionRenderer extends BaseRenderer<Row2DProjectionRendererConfig> {
  constructor(theme: Theme, config?: Partial<Row2DProjectionRendererConfig>) {
    super(theme, {
      palletSpacing: 6,
      levelSpacing: 3, // Half of palletSpacing
      maxPalletSize: 40,
      topMargin: 10,
      titleHeight: 25,
      labelMargin: 40,
      bottomLabelHeight: 25,
    }, config);
  }

  /**
   * Get the current renderer configuration
   */
  public getConfig(): Row2DProjectionRendererConfig {
    return { ...this.config };
  }

  /**
   * Extract levels that actually exist in a row
   */
  public static getRowLevels(rowPlaces: Place[]): Set<number> {
    const rowLevels = new Set<number>();
    rowPlaces.forEach(place => {
      rowLevels.add(place.level);
    });
    return rowLevels;
  }

  /**
   * Get min and max place numbers for a row
   */
  public static getRowPlaceNumberRange(rowPlaces: Place[]): { min: number; max: number; count: number } {
    if (rowPlaces.length === 0) {
      return { min: 0, max: 0, count: 0 };
    }
    const placeNumbers = [...new Set(rowPlaces.map(p => p.placeNumber))];
    const min = Math.min(...placeNumbers);
    const max = Math.max(...placeNumbers);
    return { min, max, count: max - min + 1 };
  }

  /**
   * Adjusts color brightness by a factor
   */
  private adjustBrightness(color: string, factor: number): string {
    const num = parseInt(color.replace('#', ''), 16);
    let r = (num >> 16);
    let g = ((num >> 8) & 0x00FF);
    let b = (num & 0x0000FF);
    r = Math.min(255, Math.floor(r * factor));
    g = Math.min(255, Math.floor(g * factor));
    b = Math.min(255, Math.floor(b * factor));
    return `rgb(${r}, ${g}, ${b})`;
  }

  /**
   * Draws a square pallet for the 2D projection
   */
  private drawSquarePallet(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    color: string,
    borderColor: string,
    borderWidth: number,
    place: Place
  ): void {
    // Fill the square
    ctx.fillStyle = color;
    ctx.fillRect(x, y, size, size);

    // Draw border
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = borderWidth;
    ctx.strokeRect(x, y, size, size);

    // Store click geometry for 2D projection interaction
    place.clickGeometry2D = {
      levelX: x,
      levelY: y,
      placeWidth: size,
      placeHeight: size,
      isoDepthX: 0,
      isoDepthY: 0
    };
  }

  /**
   * Draws level label on the left side of the column
   */
  private drawLevelLabel(
    ctx: CanvasRenderingContext2D,
    level: number,
    x: number,
    y: number,
    size: number
  ): void {
    const levelText = `L${level}`;
    const textSize = Math.min(size * 0.8, 20);

    ctx.fillStyle = this.theme.palette.secondary.main;
    ctx.font = `bold ${textSize.toFixed(1)}px ${this.theme.typography.fontFamily}`;
    ctx.font = `bold ${textSize.toFixed(1)}px ${this.theme.typography.fontFamily}`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(levelText, x - 10, y + size / 2);
  }

  /**
   * Draws place number label below the column
   */
  private drawPlaceNumberLabel(
    ctx: CanvasRenderingContext2D,
    placeNumber: number,
    x: number,
    y: number,
    size: number
  ): void {
    const placeText = `P${placeNumber}`;
    const textSize = Math.min(size * 0.7, 18);

    ctx.fillStyle = this.theme.palette.storageUi.highlightOrange;
    ctx.font = `bold ${textSize.toFixed(1)}px ${this.theme.typography.fontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(placeText, x + size / 2, y + 5);
  }

  /**
   * Renders the 2D projection of a selected row
   */
  render(
    ctx: CanvasRenderingContext2D,
    canvasWidth: number,
    _canvasHeight: number,
    selectedRow: RowBoundary,
    places: Map<string, Place>,
    activeLevels: Set<number>,
    rackBoundaries: RackBoundary[],
    selectedPlaceIds: Set<string> = new Set()
  ): void {
    const {
      palletSpacing,
      levelSpacing,
      maxPalletSize,
      topMargin,
      titleHeight,
      labelMargin,
      bottomLabelHeight
    } = this.config;

    // Filter places for this row (same filtering logic as PlaceRenderer)
    const rowPlaces = Array.from(places.values()).filter(p => {
      // Filter by active levels
      if (!activeLevels.has(p.level)) return false;

      const rowId = getRowId(p);
      // Use exact match instead of includes to avoid matching A16 with A001, A002, etc.
      return selectedRow.name === rowId;
    });

    if (rowPlaces.length === 0) {
      return; // Don't render anything if no places
    }

    // Group by place number (column)
    const columnMap = new Map<number, Place[]>();
    rowPlaces.forEach(place => {
      if (!columnMap.has(place.placeNumber)) {
        columnMap.set(place.placeNumber, []);
      }
      columnMap.get(place.placeNumber)!.push(place);
    });

    // Find min and max place numbers to show all columns (including deleted places)
    const placeNumbers = Array.from(columnMap.keys());
    const minPlaceNumber = Math.min(...placeNumbers);
    const maxPlaceNumber = Math.max(...placeNumbers);

    // Create array of all place numbers from min to max (to show gaps)
    const allPlaceNumbers: number[] = [];
    for (let i = minPlaceNumber; i <= maxPlaceNumber; i++) {
      allPlaceNumbers.push(i);
    }

    // Get levels that actually exist in this row (not all active levels from filter)
    const rowLevels = Row2DProjectionRenderer.getRowLevels(rowPlaces);

    // Get sorted array of row levels for iteration and positioning
    const sortedActiveLevels = Array.from(rowLevels).sort((a, b) => a - b);
    const activeLevelCount = sortedActiveLevels.length;

    if (activeLevelCount === 0) {
      return; // Don't render if no levels in this row
    }

    // Calculate dimensions for compact display
    const columnCount = allPlaceNumbers.length;
    const totalSpacing = (columnCount - 1) * palletSpacing;

    // Limit pallet size to maxPalletSize
    const palletSize = Math.min(maxPalletSize, (canvasWidth - labelMargin - 40 - totalSpacing) / columnCount);

    // Calculate total dimensions based on number of active levels
    const totalWidth = columnCount * palletSize + (columnCount - 1) * palletSpacing;
    const totalLevelSpacing = (activeLevelCount - 1) * levelSpacing;
    const totalHeight = activeLevelCount * palletSize + totalLevelSpacing;
    const projectionHeight = titleHeight + totalHeight + topMargin * 2 + bottomLabelHeight;

    // Draw semi-transparent background bar at the top
    ctx.fillStyle = alpha(this.theme.palette.storageUi.backgroundSecondary, 0.5);
    ctx.fillRect(0, 0, canvasWidth, projectionHeight);

    // Position the projection in the top center
    const startX = labelMargin + (canvasWidth - labelMargin - totalWidth) / 2;
    const startY = titleHeight + topMargin;

    // Filter rack boundaries for this row
    const rowRackBoundaries = (rackBoundaries || []).filter(rack => rack.rowName === selectedRow.name);

    // Draw each column
    allPlaceNumbers.forEach((placeNumber, columnIdx) => {
      const columnX = startX + columnIdx * (palletSize + palletSpacing);

      // Get places for this column (may be empty for deleted places)
      const columnPlaces = columnMap.get(placeNumber) || [];

      // Sort places by level (ascending)
      const sortedPlaces = columnPlaces.sort((a, b) => a.level - b.level);

      // Draw each level in the column (from bottom to top)
      sortedActiveLevels.forEach((level, levelIdx) => {
        const place = sortedPlaces.find(p => p.level === level);
        // Position from bottom to top: index 0 (lowest level) at bottom, last index at top
        // Include spacing between levels
        const palletY = startY + (activeLevelCount - 1 - levelIdx) * (palletSize + levelSpacing);

        if (place) {
          // Determine color (same as stacked view)
          let baseColor = this.theme.palette.storageUi.empty;
          if (place.occupied) {
            baseColor = this.theme.palette.storageUi.occupied;
          }
          if (place.blocked) {
            baseColor = this.theme.palette.storageUi.blocked;
          };

          const borderColor = this.adjustBrightness(baseColor, 0.75);

          // Check if this place is selected
          const isSelected = selectedPlaceIds.has(place.place_id);

          // Use place-specific selection config if available, otherwise use default
          const selectionBorderColor = place.placeSelectionBorderColor || this.theme.palette.storageUi.textPrimary;
          const selectionBorderWidth = place.placeSelectionBorderWidth || 3;

          // Determine border styling based on state
          let finalBorderColor: string;
          let borderWidth: number;
          
          if (isSelected) {
            finalBorderColor = selectionBorderColor;
            borderWidth = selectionBorderWidth;
          } else {
            finalBorderColor = borderColor;
            borderWidth = 1;
          }

          // Draw the pallet
          this.drawSquarePallet(
            ctx,
            columnX,
            palletY,
            palletSize,
            baseColor,
            finalBorderColor,
            borderWidth,
            place
          );

          // Add glow effect for selected places using shadowBlur = strokeWidth * 2
          if (isSelected) {
            ctx.save();
            ctx.shadowColor = selectionBorderColor;
            ctx.shadowBlur = selectionBorderWidth * 2;
            ctx.strokeStyle = selectionBorderColor;
            ctx.lineWidth = selectionBorderWidth;
            ctx.strokeRect(columnX, palletY, palletSize, palletSize);
            ctx.restore();
          }
        } else {
          // Draw empty slot with dashed border (deleted place in this column)
          ctx.strokeStyle = this.theme.palette.storageUi.backgroundPrimary;
          ctx.lineWidth = 1;
          ctx.setLineDash([5, 5]);
          ctx.strokeRect(columnX, palletY, palletSize, palletSize);
          ctx.setLineDash([]);
        }
      });

      // Draw level labels on the left (only for the first column)
      if (columnIdx === 0) {
        sortedActiveLevels.forEach((level, levelIdx) => {
          const labelY = startY + (activeLevelCount - 1 - levelIdx) * (palletSize + levelSpacing);
          this.drawLevelLabel(ctx, level, startX - palletSpacing, labelY, palletSize);
        });
      }

      // Draw place number label below the column
      const bottomY = startY + totalHeight;
      this.drawPlaceNumberLabel(ctx, placeNumber, columnX, bottomY, palletSize);
    });

    // Draw vertical rack separator lines based on rack boundaries
    if (rowRackBoundaries.length > 1) {
      // Sort rack boundaries by startPlace to ensure correct order
      const sortedRacks = [...rowRackBoundaries].sort((a, b) => a.startPlace - b.startPlace);

      sortedRacks.forEach((rack, rackIdx) => {
        // Skip the first rack (no separator before it)
        if (rackIdx === 0) return;

        // Find the column index for this rack's start place
        const separatorPlaceNumber = rack.startPlace;
        const columnIdx = allPlaceNumbers.indexOf(separatorPlaceNumber);

        if (columnIdx >= 0) {
          // Draw separator before this rack (between previous rack and this one)
          const separatorX = startX + columnIdx * (palletSize + palletSpacing) - palletSpacing / 2;

          ctx.strokeStyle = this.theme.palette.primary.main;
          ctx.lineWidth = 2;
          ctx.setLineDash([]);
          ctx.beginPath();
          ctx.moveTo(separatorX, startY - 5);
          ctx.lineTo(separatorX, startY + totalHeight + 5);
          ctx.stroke();
        }
      });
    }
  }
}
